// The grid's columns: the PE PRODUCTION CHECK OFF headers in the sheet's order, Review and
// Routing window in front, Ship pinned right (spec §3.5). Widths and the hidden-by-default set
// follow mockup V7. A Deposco line fills the same columns from its SOs (`deposco.ts`); its own
// (Deposco status, DLIX, CO, Group) show when the Source filter is Deposco, A2000's (WMS #, Shipped in
// A2000) hide then.

import {
  EXCEL_SOURCE_LABEL,
  REVIEW_LABELS,
  ROUTING_STATUS_LIST,
  a2000Value,
  divisionFilterLabel,
  divisionFilterValue,
  appointmentCell,
  splitValues,
  valuesText,
  shownCustomers,
  deposcoRank,
  deposcoShippedValue,
  deposcoStatusText,
  formatDay,
  formatShortDay,
  poText,
  ptText,
  stateTabOf,
  PE_ASSIGNED_LIST,
  PE_COMPLETE,
  PE_IN_PROCESS,
  PE_LABELS,
  PE_STATUS_LIST,
  SHIP_DATE_RULE,
  estimateText,
  momentText,
  peShipDateSort,
  peShipDateText,
} from "@app/shared";
import type { ChangedField, ListName, PeCell, Row, WarehouseField } from "@app/shared";

/** grey = from the systems (locked), calc = worked out by the tool, edit = the warehouse fills it in. */
export type CellKind = "system" | "calc" | "edit";
/** moment: a day and a time; estimate: minutes, typed "1h 15m"; whole: a whole number. */
export type Editor = "text" | "date" | "select" | "list" | "suggest" | "moment" | "estimate" | "whole";

export interface ColumnDef {
  id: string;
  header: string;
  kind: CellKind;
  width: number;
  /** The warehouse field an edit writes. */
  field?: WarehouseField;
  editor?: Editor;
  list?: ListName | typeof ROUTING_STATUS_LIST;
  /** A value the app works out, shown muted until someone edits the cell (`pe-order.ts`). */
  prefill?: (row: Row) => PeCell;
  /** Shown old → new, marked, when it changed since approval (`LineView.changes`, `ptChanges`). */
  change?: ChangedField;
  align?: "right";
  hidden?: boolean;
  managerOnly?: boolean;
  /** Pinned left (always shown). */
  sticky?: boolean;
  /** The header may take two lines (a narrow pinned column: "Routing window"). */
  wrapHeader?: boolean;
  /** Deposco's own: hidden by default unless the Source filter is Deposco. */
  deposco?: boolean;
  /** A2000's own: hidden by default when the Source filter is Deposco. */
  a2000?: boolean;
  /** No "Show values" in its header: the toolbar's filter does it (Source). */
  noFilter?: boolean;
  /** Where it comes from, on the header's hover. */
  source?: string;
  /** The value for sorting, filtering ("Show values") and plain display. */
  value: (row: Row) => string;
  /** How "Show values" lists a row's value when the cell shows something else (a code, an abbreviation). */
  label?: (row: Row) => string;
  /**
   * A row's several values, each its own in "Show values" (each division, each Work Needed value):
   * the filter keeps the row if ANY is kept. Without it, `value` (and `label`) is the one.
   */
  values?: (row: Row) => { value: string; label?: string }[];
  /** More a header filter matches but never lists (a division's name alone, as Ask AI sets it). */
  matchAlso?: (row: Row) => string[];
  sortValue?: (row: Row) => string | number;
}

const TYPED = "Typed by the warehouse";
const day = (d: string | null | undefined) => (d && /^\d{4}-/.test(d) ? formatDay(d) : (d ?? ""));
const num = (n: number) => n.toLocaleString("en-US");
/** A Mixed line's several values: the first, then "+N more" (all of them on hover, `grid.tsx`). */
export const firstPlus = (values: readonly string[]) =>
  values.length > 1 ? `${values[0]} +${values.length - 1} more` : (values[0] ?? "");
/** "ROSSSTORES — ROSS STORES": a value as "Show values" and the Customer filter list it. */
export const codeAndName = (code: string, name: string) =>
  code && name && code !== name ? `${code} — ${name}` : code || name;
/** Dates: the earliest, then "+N more". */
const daysPlus = (first: string | null, all: readonly string[]) => {
  const n = all.filter(Boolean).length;
  return n > 1 ? `${day(first)} +${n - 1} more` : day(first);
};

export const COLUMNS: ColumnDef[] = [
  {
    id: "review",
    header: "Review",
    kind: "calc",
    // The badge as it is: "Approved", "Updated", "No WMS", APPROVE on hover.
    width: 84,
    sticky: true,
    value: (r) => REVIEW_LABELS[r.line.review],
  },
  {
    id: "window",
    header: "Routing window",
    kind: "calc",
    // A dot and the short text (`windowShort`: "Open · 11/06"); the whole sentence on hover.
    width: 104,
    sticky: true,
    wrapHeader: true,
    value: (r) => r.window.label,
    sortValue: (r) => r.window.left ?? 9999,
  },
  {
    id: "customer",
    header: "CUSTOMER",
    kind: "system",
    // Cut with an ellipsis, the whole name (and the original, its code) on hover.
    width: 128,
    sticky: true,
    source:
      "Its shown name (Admin → Customers), else VR_PICKTKT_LI.CUSTOMER (the code) · Deposco: customer_name; the original name · code · source on hover (a Mixed WMS: several)",
    // The shown name (`customerFace`, `grid.tsx`): customers sharing one are one value of "Show values";
    // filters, search and Ask AI match the original name too (`matchAlso`). Sorted by what it shows.
    value: (r) => firstPlus(shownCustomers(r.line)),
    values: (r) => shownCustomers(r.line).map((value) => ({ value })),
    matchAlso: (r) => [...r.line.customerNames, ...r.line.customers.map((c) => c.code)],
    sortValue: (r) => shownCustomers(r.line)[0] ?? "",
  },
  {
    // A badge on every line; the toolbar's Source filter filters it.
    id: "source",
    header: "Source",
    kind: "system",
    width: 86,
    noFilter: true,
    source: "A2000: one WMS # · Deposco: one customer + PO, or + DC once it has a group",
    value: (r) => r.line.source,
  },
  {
    id: "division",
    header: "DIVISION",
    kind: "system",
    width: 120,
    source:
      "DIV → bronze_a2000_vr_division.DIV_NAME, as its abbreviation (Admin → Divisions); the name on hover · Deposco: the business unit",
    // As the customer: the full name filters, the abbreviation shows and sorts.
    value: (r) => firstPlus(r.line.divisions.length ? r.line.divisions : [r.line.division]),
    label: (r) => codeAndName(firstPlus(r.line.divs.map((d) => d.abbrev)), firstPlus(r.line.divs.map((d) => d.name))),
    // Each division its own in "Show values", by its code ("AXNY · H — …"): a Mixed line is kept
    // under any of them. A name alone (Ask AI) keeps every division of it (`matchAlso`).
    values: (r) =>
      r.line.divs.length
        ? [...new Map(r.line.divs.map((d) => [divisionFilterValue(d), divisionFilterLabel(d)])).entries()].map(
            ([value, label]) => ({ value, label }),
          )
        : [{ value: r.line.division }],
    matchAlso: (r) => r.line.divisions,
    sortValue: (r) => r.line.divs[0]?.abbrev ?? "",
  },
  {
    id: "ptDate",
    header: "RECEIVED DATE",
    kind: "system",
    width: 142,
    change: "ptDate",
    source: "VR_PICKTKT_LI.PT_DATE · Deposco: received_date",
    // The earliest, "+N more"; the date filter keeps the line if any is in range.
    value: (r) => daysPlus(r.line.ptDates[0] ?? r.line.ptDate, r.line.ptDates),
    sortValue: (r) => r.line.ptDates[0] ?? r.line.ptDate ?? "",
  },
  {
    id: "start",
    header: "FIRST SHIP DATE",
    kind: "system",
    width: 150,
    change: "start",
    source: "VR_PICKTKT_LI.START_DATE (the earliest) · Deposco: start_date",
    value: (r) => daysPlus(r.line.start, r.line.starts),
    sortValue: (r) => r.line.start ?? "",
  },
  {
    id: "cancel",
    header: "CANCEL DATE",
    kind: "system",
    width: 136,
    change: "cancel",
    source: "VR_PICKTKT_LI.CANCEL_DATE (the earliest) · Deposco: cancel_date",
    value: (r) => daysPlus(r.line.cancel, r.line.cancels),
    sortValue: (r) => r.line.cancel ?? "",
  },
  {
    id: "readyDate",
    header: "READY DATE",
    kind: "edit",
    width: 126,
    field: "readyDate",
    editor: "date",
    change: "readyDate",
    value: (r) => day(r.line.fields.readyDate),
    sortValue: (r) => r.line.fields.readyDate,
  },
  // SO / PT # and PO side by side, never merged (Isaac, V7). A workbook row keeps its "PO / PT #"
  // as typed, under SO / PT #: nothing is split or worked out of it.
  {
    id: "pt",
    header: "SO / PT #",
    kind: "system",
    width: 200,
    change: "pts",
    source: "VR_PICKTKT_LI.PICKTKT · Deposco: the SO #s",
    value: (r) => r.line.excel?.poPt ?? ptText(r.line),
  },
  {
    id: "po",
    header: "PO",
    kind: "system",
    width: 170,
    change: "po",
    source: "VR_PICKTKT_LI.ORDER_NO (the customer PO) · Deposco: customer_po",
    value: (r) => (r.line.excel ? "" : poText(r.line)),
  },
  {
    // One line per WMS # (V7 step 2): the line's own; a Mixed chip beside it (`grid.tsx`).
    id: "wms",
    header: "WMS #",
    kind: "system",
    width: 120,
    a2000: true,
    source: "VR_PICKTKT_LI.WH_SHIPMENT: one line per WMS #",
    value: (r) => r.line.wmsNo || r.line.wms,
  },
  {
    // Read-only: the SOs' highest status, MIXED when they differ (`grid.tsx`: each SO on hover).
    id: "deposcoStatus",
    header: "Deposco status",
    kind: "system",
    width: 190,
    deposco: true,
    source:
      "deposco_status: the highest of the line's SOs (New < Released < Back Ordered < Picking < Pick Complete < Partially Staged < Partially Shipped < Ship Complete); MIXED when they differ",
    value: (r) => (r.line.deposco?.status ? deposcoStatusText(r.line.deposco.status) : ""),
    sortValue: (r) => (r.line.deposco?.status ? deposcoRank(r.line.deposco.status.status) : -2),
  },
  {
    // A flag only (`a2000.ts`): the chip; empty when no pick ticket is shipped. The value is the
    // header filter's and Ask AI's: All, Some, No.
    id: "a2000",
    header: "Shipped in A2000",
    kind: "system",
    width: 196,
    a2000: true,
    source: "WMS status SHIPPED or BILLED (vw_routing_pick_ticket_shipments) — a flag: the team still marks shipped",
    value: (r) => a2000Value(r.line.a2000),
    sortValue: (r) => (r.line.a2000 ? `${r.line.a2000.all ? 0 : 1}${r.line.a2000.latest ?? ""}` : "9"),
  },
  {
    // As Shipped in A2000: a flag only (All, Some, No); the chip in `grid.tsx`.
    id: "dshipped",
    header: "Shipped in Deposco",
    kind: "system",
    width: 196,
    deposco: true,
    source: "deposco_status Ship Complete, or actual_ship_date — a flag: the team still marks shipped",
    value: (r) => (r.line.source === "Deposco" ? deposcoShippedValue(r.line.deposco?.shipped ?? null) : ""),
    sortValue: (r) => {
      const d = r.line.deposco?.shipped;
      return d ? `${d.all ? 0 : 1}${d.latest ?? ""}` : "9";
    },
  },
  {
    id: "qty",
    header: "QTY",
    kind: "system",
    width: 112,
    change: "qty",
    align: "right",
    source: "VR_PICKTKT_LI.PICK_QTY · Deposco: qty, summed",
    value: (r) => num(r.line.qty),
    sortValue: (r) => r.line.qty,
  },
  {
    id: "workNeeded",
    header: "Work Needed",
    kind: "edit",
    width: 210,
    field: "workNeeded",
    editor: "list",
    list: "Work needed",
    // Several values (`lists.ts`): the cell and an export say them comma-separated.
    value: (r) => valuesText(r.line.fields.workNeeded),
    values: (r) => splitValues(r.line.fields.workNeeded).map((value) => ({ value })),
  },
  {
    id: "productionNotes",
    header: "Production Notes",
    kind: "edit",
    width: 160,
    field: "productionNotes",
    editor: "suggest",
    value: (r) => r.line.fields.productionNotes,
  },
  {
    id: "routingStatus",
    header: "Routing status",
    kind: "edit",
    width: 146,
    field: "routingStatus",
    editor: "select",
    // A managed list since 2026-10-28: its values' categories are what the rules read.
    list: ROUTING_STATUS_LIST,
    value: (r) => r.line.fields.routingStatus,
  },
  {
    id: "routingNotes",
    header: "Routing Notes",
    kind: "edit",
    width: 200,
    field: "routingNotes",
    editor: "text",
    value: (r) => r.line.fields.routingNotes,
  },
  {
    id: "routingRef",
    header: "Routing Reference",
    kind: "edit",
    width: 160,
    field: "routingRef",
    editor: "text",
    value: (r) => r.line.fields.routingRef,
  },
  {
    id: "schedShip",
    header: "SCHEDULED SHIP DATE",
    kind: "edit",
    width: 170,
    field: "schedShip",
    editor: "date",
    change: "schedShip",
    value: (r) => day(r.line.fields.schedShip),
    sortValue: (r) => r.line.fields.schedShip,
  },
  {
    id: "appointmentDate",
    header: "Appointment date",
    kind: "edit",
    width: 140,
    field: "appointmentDate",
    editor: "date",
    source: "The date of the pickup the line is on: entering one schedules the line (Shipping)",
    // The pickup's date (item 8); a date stored before on a line on no pickup shows muted (`grid.tsx`).
    value: (r) => day(appointmentCell(r.line).value),
    sortValue: (r) => appointmentCell(r.line).value,
  },
  {
    id: "inventoryNotes",
    header: "Inventory Notes",
    kind: "edit",
    width: 124,
    field: "inventoryNotes",
    editor: "list",
    list: "Inventory notes",
    value: (r) => r.line.fields.inventoryNotes,
  },
  {
    id: "lead",
    header: "Lead",
    kind: "edit",
    width: 92,
    field: "lead",
    editor: "list",
    list: "Lead",
    value: (r) => r.line.fields.lead,
  },
  {
    id: "timing",
    header: "TIMMING FOR REWORK",
    kind: "edit",
    width: 170,
    field: "timing",
    editor: "text",
    value: (r) => r.line.fields.timing,
  },
  {
    id: "notes",
    header: "INV.",
    kind: "edit",
    width: 220,
    field: "notes",
    editor: "text",
    value: (r) => r.line.fields.notes,
  },
  // The PE ORDERS columns (`pe-order.ts`, 2026-10-30): the warehouse's own, hidden until a person
  // shows them (Columns). A prefill shows muted, its source on hover, until someone edits the cell.
  {
    id: "peShipDate",
    header: PE_LABELS.peShipDate,
    kind: "edit",
    width: 150,
    field: "peShipDate",
    editor: "date",
    hidden: true,
    source: `${SHIP_DATE_RULE} until a date or ASAP is picked; cleared, the calculation is back`,
    prefill: (r) => r.pe.shipDate,
    value: (r) => peShipDateText(r.pe.shipDate.value),
    sortValue: (r) => peShipDateSort(r.pe.shipDate.value),
  },
  {
    id: "pullGroup",
    header: PE_LABELS.pullGroup,
    kind: "edit",
    width: 150,
    field: "pullGroup",
    editor: "text",
    hidden: true,
    source: TYPED,
    value: (r) => r.line.fields.pullGroup,
  },
  {
    id: "locQty",
    header: PE_LABELS.locQty,
    kind: "edit",
    width: 130,
    field: "locQty",
    editor: "text",
    hidden: true,
    source: TYPED,
    value: (r) => r.line.fields.locQty,
  },
  {
    id: "peAssignedTo",
    header: PE_LABELS.peAssignedTo,
    kind: "edit",
    width: 170,
    field: "peAssignedTo",
    editor: "list",
    list: PE_ASSIGNED_LIST,
    hidden: true,
    source: `${TYPED}: one or more of the ${PE_ASSIGNED_LIST} list`,
    value: (r) => valuesText(r.line.fields.peAssignedTo),
    values: (r) => splitValues(r.line.fields.peAssignedTo).map((value) => ({ value })),
  },
  {
    id: "peEstimate",
    header: PE_LABELS.peEstimate,
    kind: "edit",
    width: 130,
    field: "peEstimate",
    editor: "estimate",
    align: "right",
    hidden: true,
    source: `${TYPED}: minutes or hours (45, 45m, 1h 15m, 1:15)`,
    value: (r) => estimateText(r.line.fields.peEstimate),
    sortValue: (r) => (r.line.fields.peEstimate ? Number(r.line.fields.peEstimate) : -1),
  },
  {
    id: "peStatus",
    header: PE_LABELS.peStatus,
    kind: "edit",
    width: 140,
    field: "peStatus",
    editor: "list",
    list: PE_STATUS_LIST,
    hidden: true,
    source:
      "Deposco: from the SOs' status (Released, Back Ordered, Wave Built → PENDING; Picking → IN PROCESS; Pick Complete and later → COMPLETE; all Canceled → CANCELLED) · A2000: COMPLETE once every pick ticket shipped in A2000 · else typed. IN PROCESS sets Start Time, COMPLETE End Time, when empty",
    prefill: (r) => r.pe.status,
    value: (r) => r.pe.status.value,
  },
  {
    id: "peStartAt",
    header: PE_LABELS.peStartAt,
    kind: "edit",
    width: 150,
    field: "peStartAt",
    editor: "moment",
    hidden: true,
    source: `${TYPED}; set when the status is set to ${PE_IN_PROCESS} and it is empty`,
    value: (r) => momentText(r.line.fields.peStartAt),
    sortValue: (r) => r.line.fields.peStartAt,
  },
  {
    id: "peEndAt",
    header: PE_LABELS.peEndAt,
    kind: "edit",
    width: 150,
    field: "peEndAt",
    editor: "moment",
    hidden: true,
    source: `${TYPED}; set when the status is set to ${PE_COMPLETE} and it is empty`,
    value: (r) => momentText(r.line.fields.peEndAt),
    sortValue: (r) => r.line.fields.peEndAt,
  },
  {
    id: "pallets",
    header: PE_LABELS.pallets,
    kind: "edit",
    width: 130,
    field: "pallets",
    editor: "whole",
    align: "right",
    hidden: true,
    source: `${TYPED}: one value per line, the Shipping schedule's PLTS too`,
    value: (r) => r.line.fields.pallets,
    sortValue: (r) => (r.line.fields.pallets ? Number(r.line.fields.pallets) : -1),
  },
  {
    id: "peComments",
    header: PE_LABELS.peComments,
    kind: "edit",
    width: 220,
    field: "peComments",
    editor: "text",
    hidden: true,
    source: TYPED,
    value: (r) => r.line.fields.peComments,
  },
  {
    id: "routedOn",
    header: "Routed on",
    kind: "edit",
    width: 98,
    field: "routedOn",
    editor: "date",
    hidden: true,
    value: (r) => day(r.line.fields.routedOn),
    sortValue: (r) => r.line.fields.routedOn,
  },
  {
    id: "routeBy",
    header: "Route by",
    kind: "calc",
    width: 92,
    hidden: true,
    value: (r) => day(r.window.routeBy),
    sortValue: (r) => r.window.routeBy ?? "",
  },
  {
    id: "opens",
    header: "Window opens",
    kind: "calc",
    width: 110,
    hidden: true,
    value: (r) => day(r.window.opens),
    sortValue: (r) => r.window.opens ?? "",
  },
  {
    // The same value as PO since v3: hidden, kept for a search by order number.
    id: "orderNo",
    header: "Order #",
    kind: "system",
    width: 100,
    hidden: true,
    source: "VR_PICKTKT_LI.ORDER_NO (= PO)",
    value: (r) => r.line.orderNos.join(", "),
  },
  {
    id: "wh",
    header: "Warehouse",
    kind: "system",
    width: 96,
    source: "VR_PICKTKT_LI.WH (PE = Perris · HT = Hightech 3PL · SM) · Deposco: the facility (PRS = PE)",
    value: (r) => r.line.wh,
  },
  {
    // For the header filter and Ask AI: the chip on the WMS # says it on screen.
    id: "mixed",
    header: "Mixed",
    kind: "calc",
    width: 80,
    hidden: true,
    source: "Several customers, divisions, warehouses or dates in one WMS (allowed)",
    value: (r) => (r.line.mixed ? "Yes" : "No"),
  },
  {
    // A2000: shown, not grouped by. Deposco: part of the line (customer + PO + DC).
    id: "dc",
    header: "DC",
    kind: "system",
    width: 200,
    hidden: true,
    deposco: true,
    source: "A2000 DC name, else its number (Belk, Macy's, Walmart USA, Navy Exchange…: 8 customers) · Deposco: dc_no",
    value: (r) => r.line.dcs.join(", "),
  },
  {
    id: "dlix",
    header: "DLIX",
    kind: "system",
    width: 130,
    hidden: true,
    deposco: true,
    source: "Deposco dlix",
    value: (r) => r.line.deposco?.dlixes.join(", ") ?? "",
  },
  {
    id: "co",
    header: "CO",
    kind: "system",
    width: 130,
    hidden: true,
    deposco: true,
    source: "Deposco co",
    value: (r) => r.line.deposco?.cos.join(", ") ?? "",
  },
  {
    id: "group",
    header: "Group",
    kind: "system",
    width: 190,
    hidden: true,
    deposco: true,
    source: "Deposco group_name: an SO with a DC is a line once it has one",
    value: (r) => r.line.deposco?.groups.join(", ") ?? "",
  },
  {
    id: "value",
    header: "Order value",
    kind: "calc",
    width: 108,
    hidden: true,
    managerOnly: true,
    align: "right",
    source: "A2000 line VALUE, summed per pick ticket",
    value: (r) => (r.line.value === null ? "" : `$${num(Math.round(r.line.value))}`),
    sortValue: (r) => r.line.value ?? -1,
  },
];

/** Worklist only. */
export const OPEN_COLUMNS: ColumnDef[] = [
  {
    // For the header filter and Ask AI: two tabs at once ("route today": Past deadline + Route now).
    id: "stateTab",
    header: "Tab",
    kind: "calc",
    width: 140,
    hidden: true,
    source: "The Worklist tab the line is in by its routing window",
    value: (r) => (r.line.finished ? "" : stateTabOf(r)),
  },
];

/**
 * History's first column, in Review's place: how the line finished, as a badge with its day
 * ("Shipped 10/22", `FinishedChip`). Its value is the status alone, for the header filter and Ask AI.
 */
export const FINISHED_COLUMN: ColumnDef = {
  id: "finished",
  header: "Finished",
  kind: "calc",
  // "Shipped 11/02"; a longer one cut, whole on hover.
  width: 96,
  sticky: true,
  value: (r) => r.line.finished?.status ?? "",
  sortValue: (r) => `${r.line.finished?.status ?? ""}|${r.line.finished?.at ?? ""}`,
};

/** History only: where the line comes from, and when and by whom it finished. */
export const FINISHED_COLUMNS: ColumnDef[] = [
  {
    id: "origin",
    header: "Origin",
    kind: "calc",
    width: 96,
    source: `App: this tool's lines · ${EXCEL_SOURCE_LABEL}: the old workbook's "Shipped Orders 2026" tab`,
    value: (r) => (r.line.excel ? EXCEL_SOURCE_LABEL : "App"),
  },
  {
    id: "finishedAt",
    header: "Finished on",
    kind: "calc",
    width: 132, // room for "approx." on a workbook row
    value: (r) => day(r.line.finished?.at),
    sortValue: (r) => r.line.finished?.at ?? "",
  },
  { id: "finishedBy", header: "Finished by", kind: "calc", width: 130, value: (r) => r.line.finished?.by ?? "" },
  { id: "finishedNote", header: "Finish note", kind: "calc", width: 220, value: (r) => r.line.finished?.note ?? "" },
];

export const SELECT_WIDTH = 34;
export const EXPAND_WIDTH = 34;
/** The Worklist's Ship, pinned right: a "Ship" button (Mark shipped on hover). */
export const SHIP_WIDTH = 72;
/** Every row's Details, pinned right (left of Ship on the Worklist, alone on History): the line's details. */
export const DETAILS_WIDTH = 36;
/** The pinned right part: Details, and Ship on the Worklist. */
/** The pinned right part: Details, and Ship on the Worklist for someone who may write (a Viewer has Details only). */
export const rightWidth = (mode: string, canWrite = true) =>
  DETAILS_WIDTH + (mode === "worklist" && canWrite ? SHIP_WIDTH : 0);

/**
 * The grid's geometry: each column's left edge after select and expand, the pinned left part's
 * right edge (`pinned`), the pinned right part (`right`: Details, and Ship on the Worklist; none on
 * a phone, where it scrolls) and the whole width. A phone pins only select and expand (the caller
 * passes its columns unpinned).
 */
export function gridGeometry(
  columns: readonly Pick<ColumnDef, "width" | "sticky">[],
  mode: string,
  phone: boolean,
  canWrite = true,
) {
  const lefts: number[] = [];
  let x = SELECT_WIDTH + EXPAND_WIDTH;
  let pinned = x;
  for (const c of columns) {
    lefts.push(x);
    x += c.width;
    if (c.sticky) pinned = x;
  }
  const right = rightWidth(mode, canWrite);
  return { lefts, total: x + right, pinned, right: phone ? 0 : right };
}

/**
 * Enter on a row's first cell opens its details — but on the Worklist's Review of a line to approve,
 * where Enter approves (as a click does). Typing a character never opens them.
 */
export const enterOpensDetails = (
  col: number,
  columnId: string,
  line: { review: string; noWms: boolean },
  readOnly: boolean,
) => col === 0 && !(columnId === "review" && !readOnly && !line.noWms && line.review !== "approved");

/**
 * History's Finished badge: the date alone when the status has one ("09/19", its colour the status —
 * the tabs filter by it), else a short word ("Regrouped"); the whole of it ("Shipped 09/19/26") on
 * hover and in the line details.
 */
export function finishedBadge(finished: { status: string; at: string | null } | null | undefined): {
  text: string;
  full: string;
} {
  if (!finished) return { text: "—", full: "" };
  const dated = finished.status !== "Regrouped by WMS" && !!finished.at;
  return dated
    ? { text: formatShortDay(finished.at!), full: `${finished.status} ${formatDay(finished.at!)}` }
    : { text: finished.status === "Regrouped by WMS" ? "Regrouped" : finished.status, full: finished.status };
}

/** History's own default-hidden columns: Routing window (a finished line has none: "—" on every row). */
export const HISTORY_HIDDEN: readonly string[] = ["window"];

/**
 * The columns a page shows, in their default order. History: how the line finished in Review's place,
 * and Routing window unpinned (hidden by default, `HISTORY_HIDDEN`, still in the Columns menu).
 */
export function pageColumns(mode: "worklist" | "history"): ColumnDef[] {
  if (mode === "worklist") return [...COLUMNS, ...OPEN_COLUMNS];
  return [
    FINISHED_COLUMN,
    ...COLUMNS.filter((c) => c.id !== "review").map((c) => (c.id === "window" ? { ...c, sticky: false } : c)),
    ...FINISHED_COLUMNS,
  ];
}
